import React, { useEffect, useRef } from 'react';

/**
 * Mouse-reactive hex/square/triangle grid rendered on a <canvas>.
 * Ported from landing/components/ui/ShapeGrid.tsx (Breww / ReactBits) with
 * two improvements from the review:
 *   - Mouse listener attached to the *container* (not window) so the RAF loop
 *     doesn't wake on mouse-moves in other sections.
 *   - IntersectionObserver pauses the RAF when the section is off-screen.
 *
 * Props:
 *   direction, speed, shape, squareSize, hoverTrailAmount — same as source.
 *   borderColor, hoverFillColor — pass ESG palette colors (rgba strings work).
 */
const ShapeGridCanvas = ({
  direction = 'diagonal',
  speed = 0.35,
  borderColor = 'rgba(59,130,246,0.14)',
  squareSize = 44,
  hoverFillColor = 'rgba(16,185,129,0.35)',
  shape = 'hexagon',
  hoverTrailAmount = 4,
  className = '',
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const activeRef = useRef(true);
  const gridOffset = useRef({ x: 0, y: 0 });
  const hoveredCell = useRef(null);
  const trailCells = useRef([]);
  const cellOpacities = useRef(new Map());

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const isHex = shape === 'hexagon';
    const isTri = shape === 'triangle';
    const hexHoriz = squareSize * 1.5;
    const hexVert = squareSize * Math.sqrt(3);

    const resize = () => {
      const { clientWidth, clientHeight } = container;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(clientWidth * dpr);
      canvas.height = Math.floor(clientHeight * dpr);
      canvas.style.width = `${clientWidth}px`;
      canvas.style.height = `${clientHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const drawHex = (cx, cy, size) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        const vx = cx + size * Math.cos(angle);
        const vy = cy + size * Math.sin(angle);
        if (i === 0) ctx.moveTo(vx, vy); else ctx.lineTo(vx, vy);
      }
      ctx.closePath();
    };
    const drawCircle = (cx, cy, size) => {
      ctx.beginPath();
      ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
      ctx.closePath();
    };
    const drawTri = (cx, cy, size, flip) => {
      ctx.beginPath();
      if (flip) {
        ctx.moveTo(cx, cy + size / 2);
        ctx.lineTo(cx + size / 2, cy - size / 2);
        ctx.lineTo(cx - size / 2, cy - size / 2);
      } else {
        ctx.moveTo(cx, cy - size / 2);
        ctx.lineTo(cx + size / 2, cy + size / 2);
        ctx.lineTo(cx - size / 2, cy + size / 2);
      }
      ctx.closePath();
    };

    const draw = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      ctx.clearRect(0, 0, w, h);

      if (isHex) {
        const colShift = Math.floor(gridOffset.current.x / hexHoriz);
        const ox = ((gridOffset.current.x % hexHoriz) + hexHoriz) % hexHoriz;
        const oy = ((gridOffset.current.y % hexVert) + hexVert) % hexVert;
        const cols = Math.ceil(w / hexHoriz) + 3;
        const rows = Math.ceil(h / hexVert) + 3;
        for (let col = -2; col < cols; col++) {
          for (let row = -2; row < rows; row++) {
            const cx = col * hexHoriz + ox;
            const cy = row * hexVert + ((col + colShift) % 2 !== 0 ? hexVert / 2 : 0) + oy;
            const alpha = cellOpacities.current.get(`${col},${row}`);
            if (alpha) {
              ctx.globalAlpha = alpha;
              drawHex(cx, cy, squareSize);
              ctx.fillStyle = hoverFillColor;
              ctx.fill();
              ctx.globalAlpha = 1;
            }
            drawHex(cx, cy, squareSize);
            ctx.strokeStyle = borderColor;
            ctx.stroke();
          }
        }
      } else if (isTri) {
        const halfW = squareSize / 2;
        const colShift = Math.floor(gridOffset.current.x / halfW);
        const rowShift = Math.floor(gridOffset.current.y / squareSize);
        const ox = ((gridOffset.current.x % halfW) + halfW) % halfW;
        const oy = ((gridOffset.current.y % squareSize) + squareSize) % squareSize;
        const cols = Math.ceil(w / halfW) + 4;
        const rows = Math.ceil(h / squareSize) + 4;
        for (let col = -2; col < cols; col++) {
          for (let row = -2; row < rows; row++) {
            const cx = col * halfW + ox;
            const cy = row * squareSize + squareSize / 2 + oy;
            const flip = (((col + colShift + row + rowShift) % 2) + 2) % 2 !== 0;
            const alpha = cellOpacities.current.get(`${col},${row}`);
            if (alpha) {
              ctx.globalAlpha = alpha;
              drawTri(cx, cy, squareSize, flip);
              ctx.fillStyle = hoverFillColor;
              ctx.fill();
              ctx.globalAlpha = 1;
            }
            drawTri(cx, cy, squareSize, flip);
            ctx.strokeStyle = borderColor;
            ctx.stroke();
          }
        }
      } else if (shape === 'circle') {
        const ox = ((gridOffset.current.x % squareSize) + squareSize) % squareSize;
        const oy = ((gridOffset.current.y % squareSize) + squareSize) % squareSize;
        const cols = Math.ceil(w / squareSize) + 3;
        const rows = Math.ceil(h / squareSize) + 3;
        for (let col = -2; col < cols; col++) {
          for (let row = -2; row < rows; row++) {
            const cx = col * squareSize + squareSize / 2 + ox;
            const cy = row * squareSize + squareSize / 2 + oy;
            const alpha = cellOpacities.current.get(`${col},${row}`);
            if (alpha) {
              ctx.globalAlpha = alpha;
              drawCircle(cx, cy, squareSize);
              ctx.fillStyle = hoverFillColor;
              ctx.fill();
              ctx.globalAlpha = 1;
            }
            drawCircle(cx, cy, squareSize);
            ctx.strokeStyle = borderColor;
            ctx.stroke();
          }
        }
      } else {
        const ox = ((gridOffset.current.x % squareSize) + squareSize) % squareSize;
        const oy = ((gridOffset.current.y % squareSize) + squareSize) % squareSize;
        const cols = Math.ceil(w / squareSize) + 3;
        const rows = Math.ceil(h / squareSize) + 3;
        for (let col = -2; col < cols; col++) {
          for (let row = -2; row < rows; row++) {
            const sx = col * squareSize + ox;
            const sy = row * squareSize + oy;
            const alpha = cellOpacities.current.get(`${col},${row}`);
            if (alpha) {
              ctx.globalAlpha = alpha;
              ctx.fillStyle = hoverFillColor;
              ctx.fillRect(sx, sy, squareSize, squareSize);
              ctx.globalAlpha = 1;
            }
            ctx.strokeStyle = borderColor;
            ctx.strokeRect(sx, sy, squareSize, squareSize);
          }
        }
      }
    };

    const updateOpacities = () => {
      const targets = new Map();
      if (hoveredCell.current) targets.set(`${hoveredCell.current.x},${hoveredCell.current.y}`, 1);
      if (hoverTrailAmount > 0) {
        for (let i = 0; i < trailCells.current.length; i++) {
          const key = `${trailCells.current[i].x},${trailCells.current[i].y}`;
          if (!targets.has(key)) {
            targets.set(key, (trailCells.current.length - i) / (trailCells.current.length + 1));
          }
        }
      }
      for (const [key] of targets) {
        if (!cellOpacities.current.has(key)) cellOpacities.current.set(key, 0);
      }
      for (const [key, opacity] of cellOpacities.current) {
        const target = targets.get(key) || 0;
        const next = opacity + (target - opacity) * 0.15;
        if (next < 0.005) cellOpacities.current.delete(key);
        else cellOpacities.current.set(key, next);
      }
    };

    const tick = () => {
      if (!activeRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const eff = Math.max(speed, 0.05);
      const wrapX = isHex ? hexHoriz * 2 : squareSize;
      const wrapY = isHex ? hexVert : isTri ? squareSize * 2 : squareSize;
      switch (direction) {
        case 'right': gridOffset.current.x = (gridOffset.current.x - eff + wrapX) % wrapX; break;
        case 'left': gridOffset.current.x = (gridOffset.current.x + eff + wrapX) % wrapX; break;
        case 'up': gridOffset.current.y = (gridOffset.current.y + eff + wrapY) % wrapY; break;
        case 'down': gridOffset.current.y = (gridOffset.current.y - eff + wrapY) % wrapY; break;
        case 'diagonal':
        default:
          gridOffset.current.x = (gridOffset.current.x - eff + wrapX) % wrapX;
          gridOffset.current.y = (gridOffset.current.y - eff + wrapY) % wrapY;
          break;
      }
      updateOpacities();
      draw();
      rafRef.current = requestAnimationFrame(tick);
    };

    const cellAt = (mx, my) => {
      if (isHex) {
        const colShift = Math.floor(gridOffset.current.x / hexHoriz);
        const ox = ((gridOffset.current.x % hexHoriz) + hexHoriz) % hexHoriz;
        const oy = ((gridOffset.current.y % hexVert) + hexVert) % hexVert;
        const col = Math.round((mx - ox) / hexHoriz);
        const rowOff = (col + colShift) % 2 !== 0 ? hexVert / 2 : 0;
        return { x: col, y: Math.round((my - oy - rowOff) / hexVert) };
      }
      if (isTri) {
        const halfW = squareSize / 2;
        const ox = ((gridOffset.current.x % halfW) + halfW) % halfW;
        const oy = ((gridOffset.current.y % squareSize) + squareSize) % squareSize;
        return { x: Math.round((mx - ox) / halfW), y: Math.floor((my - oy) / squareSize) };
      }
      const ox = ((gridOffset.current.x % squareSize) + squareSize) % squareSize;
      const oy = ((gridOffset.current.y % squareSize) + squareSize) % squareSize;
      const round = shape === 'circle' ? Math.round : Math.floor;
      return { x: round((mx - ox) / squareSize), y: round((my - oy) / squareSize) };
    };

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      if (mx < 0 || mx > rect.width || my < 0 || my > rect.height) {
        hoveredCell.current = null;
        return;
      }
      const cell = cellAt(mx, my);
      const prev = hoveredCell.current;
      if (!prev || prev.x !== cell.x || prev.y !== cell.y) {
        if (prev && hoverTrailAmount > 0) {
          trailCells.current.unshift({ ...prev });
          if (trailCells.current.length > hoverTrailAmount) {
            trailCells.current.length = hoverTrailAmount;
          }
        }
        hoveredCell.current = cell;
      }
    };
    const onLeave = () => {
      if (hoveredCell.current && hoverTrailAmount > 0) {
        trailCells.current.unshift({ ...hoveredCell.current });
        if (trailCells.current.length > hoverTrailAmount) {
          trailCells.current.length = hoverTrailAmount;
        }
      }
      hoveredCell.current = null;
    };

    container.addEventListener('mousemove', onMove, { passive: true });
    container.addEventListener('mouseleave', onLeave);

    const io = new IntersectionObserver(
      ([entry]) => { activeRef.current = entry.isIntersecting; },
      { threshold: 0.01 }
    );
    io.observe(container);

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      io.disconnect();
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseleave', onLeave);
    };
  }, [direction, speed, borderColor, hoverFillColor, squareSize, shape, hoverTrailAmount]);

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 pointer-events-auto ${className}`}
      aria-hidden="true"
      style={{
        maskImage: 'radial-gradient(ellipse at center, black 55%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 55%, transparent 100%)',
      }}
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
};

export default ShapeGridCanvas;
